/**
 * LoveLink 3-Step Intro Flow
 * Beautiful onboarding that introduces Circle → LoveLink → Rewards
 * Shows once per user, gentle and culturally aligned
 */
import React, { useState } from "react";
import { Sparkles, Link2, Users } from "lucide-react";

type LoveLinkIntroFlowProps = {
  isOpen: boolean;
  onClose: () => void;
  userName?: string | null;
  loveLinkUrl?: string | null;
};

export const LoveLinkIntroFlow: React.FC<LoveLinkIntroFlowProps> = ({
  isOpen,
  onClose,
  userName,
  loveLinkUrl,
}) => {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [linkRevealed, setLinkRevealed] = useState(false);
  const displayName = userName || "Friend";

  if (!isOpen) return null;

  const goNext = () => {
    if (step === 3) {
      onClose();
      return;
    }
    setStep((prev) => (prev === 1 ? 2 : 3) as 1 | 2 | 3);
  };

  const goBack = () => {
    if (step === 1) return;
    setStep((prev) => (prev === 3 ? 2 : 1) as 1 | 2 | 3);
  };

  const handleRevealLink = () => {
    if (!linkRevealed) {
      setLinkRevealed(true);
    }
  };

  const copyLink = async () => {
    if (!loveLinkUrl) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(loveLinkUrl);
        alert("LoveLink copied! Share it when it feels right.");
      } else {
        // Fallback for older browsers
        const textarea = document.createElement("textarea");
        textarea.value = loveLinkUrl;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        alert("LoveLink copied!");
      }
    } catch (err) {
      console.warn("Failed to copy LoveLink", err);
      alert("Could not copy LoveLink. Please copy manually.");
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="relative w-full max-w-lg mx-4 rounded-3xl surface-card shadow-2xl overflow-hidden animate-in zoom-in duration-300">
        {/* Soft gradient header */}
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-br from-rose-500 via-purple-600 to-emerald-500 opacity-80 pointer-events-none" />
        
        <div className="relative p-6 sm:p-8">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-full bg-white/90 dark:bg-slate-800/90 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-slate-100 shadow-md hover:bg-white dark:hover:bg-slate-700 transition-colors"
          >
            Skip for now
          </button>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-6 text-xs font-medium text-white/90">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/30">
              {step}
            </span>
            <span>
              {step === 1 && "Your Circle"}
              {step === 2 && "Your LoveLink"}
              {step === 3 && "Circle Rewards"}
            </span>
          </div>

          {/* Card content */}
          <div className="mt-4 rounded-2xl bg-white/95 dark:bg-slate-900/95 p-5 sm:p-6 shadow-md">
            {step === 1 && (
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 rounded-full bg-rose-50 dark:bg-rose-900/40 px-3 py-1 text-xs font-medium text-rose-600 dark:text-rose-300">
                  <Users className="h-3 w-3" />
                  <span>Welcome, {displayName}</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-slate-50">
                  Your Circle starts here.
                </h2>
                <p className="text-sm text-gray-600 dark:text-slate-300 leading-relaxed">
                  UnityLove grows through real connections. Every person you
                  invite—and every person they invite—becomes part of your
                  Circle.
                </p>
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  Your Circle is your extended community inside UnityLove:
                  people who share values around love, culture, and respect.
                </p>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 rounded-full bg-purple-50 dark:bg-purple-900/40 px-3 py-1 text-xs font-medium text-purple-600 dark:text-purple-300">
                  <Link2 className="h-3 w-3" />
                  <span>Your LoveLink</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-slate-50">
                  This is your doorway into the Circle.
                </h2>
                <p className="text-sm text-gray-600 dark:text-slate-300 leading-relaxed">
                  Your LoveLink is a gentle invitation you can share when it
                  feels right. It&apos;s how new people step into your Circle.
                </p>

                {!linkRevealed && (
                  <button
                    onClick={handleRevealLink}
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-purple-700 transition-colors"
                  >
                    <Sparkles className="h-4 w-4" />
                    Reveal My LoveLink
                  </button>
                )}

                {linkRevealed && (
                  <div className="mt-3 space-y-3">
                    <div className="rounded-2xl border border-purple-100 dark:border-purple-800 bg-purple-50/60 dark:bg-purple-900/30 p-3">
                      <p className="text-xs font-medium text-purple-700 dark:text-purple-300 mb-1">
                        Your LoveLink
                      </p>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <div className="flex-1 truncate rounded-full bg-white dark:bg-slate-800 px-3 py-1.5 text-xs font-mono text-gray-800 dark:text-gray-200 border border-purple-100 dark:border-purple-700">
                          {loveLinkUrl || "Loading your LoveLink…"}
                        </div>
                        <button
                          type="button"
                          onClick={copyLink}
                          disabled={!loveLinkUrl}
                          className="inline-flex items-center justify-center rounded-full border border-purple-300 dark:border-purple-600 bg-white dark:bg-slate-800 px-3 py-1.5 text-xs font-medium text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60 transition-colors"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Share it only with people who respect love, culture, and
                      community. No pressure—just when it feels aligned.
                    </p>
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 dark:bg-emerald-900/40 px-3 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-300">
                  <Sparkles className="h-3 w-3" />
                  <span>Circle Rewards</span>
                </div>
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-slate-50">
                  A thank-you from the community.
                </h2>
                <p className="text-sm text-gray-600 dark:text-slate-300 leading-relaxed">
                  When someone in your Circle supports UnityLove with an
                  upgrade, the community sends a small thank-you your way.
                </p>
                <p className="text-sm text-gray-600 dark:text-slate-300 leading-relaxed">
                  You receive a <span className="font-semibold">10% contribution</span> from eligible
                  purchases—after a{" "}
                  <span className="font-semibold">30-day protection window</span> for refunds and
                  chargebacks.
                </p>
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  It&apos;s not a job or a hustle. It&apos;s appreciation for helping
                  grow a safe space for Black love, poly families, and real
                  connection.
                </p>
              </div>
            )}

            {/* Footer buttons */}
            <div className="mt-6 flex items-center justify-between gap-3">
              <button
                onClick={goBack}
                disabled={step === 1}
                className="text-xs font-medium text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 disabled:opacity-40 disabled:hover:text-gray-500 dark:disabled:hover:text-slate-400 transition-opacity"
              >
                {step === 1 ? "" : "Back"}
              </button>
              <div className="flex-1 flex justify-end">
                <button
                  onClick={goNext}
                  className="inline-flex items-center justify-center rounded-full bg-gray-900 dark:bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-black dark:hover:bg-rose-700 transition-colors"
                >
                  {step === 3 ? "Enter My Dashboard" : "Continue"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
